"use client"

import { useRef } from "react"
import clsx from "clsx"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"

import type { PropsWithClassName } from "~/@types/generics"
import { type TPanelSizeChart } from "~/components/ui/Product/ProductHeader/PanelSizeChart/_data/serializer"
import RichText from "~/components/ui/RichText"
import Icon from "~/components/abstracts/Icon"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { Panel, usePanel } from "~/managers/PanelManager"
import { fromBezierToArray } from "~/utils/from-bezier-to-array"

import { hideFrom, showFrom } from "~/styles/utils/show-hide"
import { easings } from "~/styles/variables/easings"

import * as css from "./styles.css"

type PanelSizeChartComponentProps = PropsWithClassName<{
  onSuccess?: () => void
  panelSizeChart: NonNullable<TPanelSizeChart>
}>

export function ClientPanelSizeChart({ className, panelSizeChart }: PanelSizeChartComponentProps) {
  const t = useTranslate()
  const panelSizeChartRef = useRef(null)
  const { title, subtitle } = panelSizeChart
  const { removeCurrent } = usePanel()

  const onPanelRemove = () => {
    removeCurrent()
  }

  return (
    <Panel className={clsx(className)} zIndex={50} clickOutsideRef={panelSizeChartRef}>
      <div className={css.PanelSizeChart} ref={panelSizeChartRef}>
        <div className={css.topHead}>
          <div
            className={showFrom("desktop", "flex", {
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <span className={css.type}>{t("size_chart_type")}</span>
            <button aria-label={t("aria_panel_size_close")} onClick={onPanelRemove}>
              <Icon width={14} name="Cross" theme="blue-deep" />
            </button>
          </div>
          <button
            aria-label={t("aria_panel_size_close")}
            className={hideFrom("desktop", "flex", { gap: 10, alignItems: "center" })}
            onClick={onPanelRemove}
          >
            <Icon width={14} name="ArrowRight" theme="blue-deep" />
            <span className={css.back}>{t("size_chart_back")}</span>
          </button>
        </div>
        <div className={css.subHead}>
          <RichText render={title} className={css.title} />
          <RichText render={subtitle} className={css.subtitle} />
        </div>

        {panelSizeChart.items.length > 0 && (
          <div className={css.accordionWrapper}>
            <Accordion>
              {panelSizeChart.items.map((accordionPanel, i) => {
                return (
                  <AccordionPanel
                    key={`size-chart-accordion-panel-${i}`}
                    index={i}
                    transition={{ duration: 0.5, ease: fromBezierToArray(easings["easeInOutAccordion"]) }}
                    className={css.panel}
                    headClassName={clsx(css.panelHead)}
                    ariaLabels={{
                      open: t("aria_size_chart_accordion_open"),
                      close: t("aria_size_chart_accordion_close"),
                    }}
                    head={(isActive) => (
                      <>
                        {accordionPanel.title}
                        <Icon name={isActive ? "ChevronUp" : "ChevronDown"} width={14} theme="blue-deep" />
                      </>
                    )}
                    body={() => (
                      <div className={clsx(css.content)}>
                        {accordionPanel?.items.map((item, bodyIndex) => {
                          return <RichText render={item} key={`accordion-content-${i}-${bodyIndex}`} />
                        })}
                      </div>
                    )}
                  />
                )
              })}
            </Accordion>
          </div>
        )}
      </div>
    </Panel>
  )
}
